import { ANALYTIC_EVENTS, CartStore, retrieveCartPriceDetailsQuery } from '@rc-d2c/context-manager';
import { useFormattedPrice } from '@rc-d2c/shared-behavior/hooks/useFormattedPrice';
import { useGlobalSettingsStore } from '@rc-d2c/shared-behavior/hooks/useStore';
import { RcButton } from '@rc-d2c/shared-ui/RcButton';
import { RcButtonIcon } from '@rc-d2c/shared-ui/RcButtonIcon';
import { RcInput } from '@rc-d2c/shared-ui/RcInput';
import { RcMessage } from '@rc-d2c/shared-ui/RcMessage';
import classnames from 'classnames/bind';
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { getErrorMsgKey } from '../../utils/errorMessageUtils';
import styles from './CartTotalPromotion.module.scss';

const cx = classnames.bind(styles);

type CartTotalPromotionProps = {
  isDisabled?: boolean;
};

export const CartTotalPromotion = ({ isDisabled }: CartTotalPromotionProps) => {
  const { t } = useTranslation();

  const { promotion, setCurrentPromotionCodeApplied, setPromotion, currentPromotionCodeApplied } = useStore(CartStore, (state) => state);
  const country = useGlobalSettingsStore.use.country();
  const { formatPrice } = useFormattedPrice();

  const [currentPromoCode, setCurrentPromoCode] = useState(promotion?.code ?? '');
  const [showInputPromoCode, setShowInputPromoCode] = useState(false);

  useEffect(
    function initPromotion() {
      if (promotion) {
        window.dataLayer.push({
          event: ANALYTIC_EVENTS.CART_PROMO_CODE_CLICK,
          [ANALYTIC_EVENTS.CART_PROMO_CODE_CLICK]: {
            status: promotion.type === 'success' ? 'Success' : 'Failure',
            promoCodeName: currentPromoCode,
          },
        });
      }

      if (!promotion || promotion.type === 'success') {
        setCurrentPromoCode('');
      }

      if (promotion?.type === 'success') {
        setShowInputPromoCode(true);
      }
    },
    [promotion],
  );

  const handleChangePromotionCode = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentPromoCode(event.target.value);
  };

  const handleOnSubmit = (event: FormEvent) => {
    event.preventDefault();
    setCurrentPromotionCodeApplied(currentPromoCode);
    retrieveCartPriceDetailsQuery();
  };

  const handleRemovePromotion = () => {
    setCurrentPromotionCodeApplied('');
    setPromotion(null);
    retrieveCartPriceDetailsQuery();
  };

  const getPromotionMsg = () => {
    if (!promotion) {
      return '';
    }
    return promotion.type === 'success'
      ? t('cart.cardTotal.promotion.success', {
          discountPrice: formatPrice(promotion.discountPrice),
          code: promotion.code,
        })
      : t(getErrorMsgKey(promotion.code));
  };

  /* @deprecated [f47ac10b-58cc-4372-a567-0e02b2c3d479] */
  useEffect(() => {
    if (country) {
      const checkoutPromotion = localStorage.getItem(`${country}-rc-promotionCode`);
      if (checkoutPromotion !== null) {
        const promotion = JSON.parse(checkoutPromotion);
        if (promotion === '') {
          handleRemovePromotion();
        } else if (promotion !== promotion?.code) {
          setCurrentPromoCode(promotion);
          setCurrentPromotionCodeApplied(promotion);
          retrieveCartPriceDetailsQuery();
        }
      }
    }
  }, []);
  /* deprecated END */

  const hasError = !isDisabled && currentPromoCode === currentPromotionCodeApplied && promotion?.type === 'error';

  return (
    <div data-testid="cart-total-promotion" className={cx('cart-total-promotion')}>
      {showInputPromoCode ? (
        <form data-testid="cart-total-promotion-form" className={cx('cart-total-promotion__form')} onSubmit={handleOnSubmit}>
          <RcInput
            onChange={handleChangePromotionCode}
            value={currentPromoCode}
            ariaLabel="Enter promotion code"
            placeholder={t('cart.cardTotal.promotionCode')}
            disabled={isDisabled}
            data-testid="cart-total-promotion-input"
            inputState={hasError ? 'error' : 'default'}
            customClasses={cx('cart-total-promotion__form__input')}
            name="cart-total-promotion-input"
          />
          <RcButton
            type="submit"
            testId="cart-total-promotion-apply"
            isDisabled={!currentPromoCode || isDisabled}
            variant="secondary"
            customClasses={cx('cart-total-promotion__form__apply')}
            label={t('cart.cardTotal.apply')}
            onClick={() => {}}
          />
        </form>
      ) : (
        <RcButton
          testId="cart-total-promotion-show"
          isDisabled={isDisabled}
          variant="hyperlink"
          label={t('cart.cardTotal.usePromoCode')}
          onClick={() => setShowInputPromoCode(true)}
        />
      )}

      <div className={cx('cart-total-promotion__msg', { 'cart-total-promotion__msg--extend': promotion })}>
        {promotion?.showMessage && showInputPromoCode && (
          <div key={promotion.code} className={cx('cart-total-promotion__msg__container')}>
            <RcMessage
              testId="cart-total-promotion-msg"
              type={promotion.type}
              message={getPromotionMsg()}
              customClasses={cx({
                'cart-total-promotion__msg--error': promotion.type === 'error',
                'cart-total-promotion__msg--success': promotion.type === 'success',
              })}
            />
            <RcButtonIcon icon="cross" variant="hyperlink" onClick={handleRemovePromotion} testId="cart-total-promotion-remove" />
          </div>
        )}
      </div>
    </div>
  );
};
